/**
 * Build dimensional child rows from an Arrow detail table (BLOX-2369).
 * Used on chevron expand so we do not flatten the full cartesian into Redux up front.
 */

import type { Field, Table } from 'apache-arrow';

/** Logical row height (px); must match `buildDimensionalDataTableItemRowV2`). */
export const BUILDER_ARROW_ROW_HEIGHT = 40;

/** Max dim tuples materialised in ReactGrid at once (sliding window). */
export const BUILDER_ARROW_WINDOW_SIZE = 150;

export type ArrowDetailChildRow = {
  id: string;
  name: string;
  chevronType: 'child';
  total_values: { name: string; value: number }[];
  indicatorId: number;
  parentId: number;
  type?: string;
};

export type BuildDimChildRowsOptions = {
  indicatorColumn: string;
  indicatorName: string;
  indicatorId: number;
  dataType?: string;
  dimColumns: string[];
  periodColumns: string[];
  /** Number of matching indicator rows to skip before materialising. */
  matchSkip?: number;
  /** Max rows to return after skip. */
  maxRows?: number;
};

/**
 * Count dim detail rows for one indicator in the block Arrow table.
 */
export function countDimChildRowsForIndicator(
  table: Table,
  indicatorColumn: string,
  indicatorName: string,
): number {
  const indicatorCol = table.getChild(indicatorColumn);
  if (!indicatorCol) return 0;

  let count = 0;
  for (let rowIndex = 0; rowIndex < table.numRows; rowIndex += 1) {
    if (indicatorCol.get(rowIndex) === indicatorName) {
      count += 1;
    }
  }
  return count;
}

/**
 * Materialise a window of child rows for one indicator from the block detail Arrow table.
 */
export function buildDimChildRowsFromArrowTable(
  table: Table,
  options: BuildDimChildRowsOptions,
): ArrowDetailChildRow[] {
  const {
    indicatorColumn,
    indicatorName,
    indicatorId,
    dataType,
    dimColumns,
    periodColumns,
    matchSkip = 0,
    maxRows = BUILDER_ARROW_WINDOW_SIZE,
  } = options;

  const indicatorCol = table.getChild(indicatorColumn);
  if (!indicatorCol) return [];

  const dimCols = dimColumns.map((name) => table.getChild(name));
  const periodCols = periodColumns.map((name) => table.getChild(name));

  const rows: ArrowDetailChildRow[] = [];
  let matchedBeforeWindow = 0;

  for (let rowIndex = 0; rowIndex < table.numRows && rows.length < maxRows; rowIndex += 1) {
    if (indicatorCol.get(rowIndex) === indicatorName) {
      if (matchedBeforeWindow < matchSkip) {
        matchedBeforeWindow += 1;
      } else {
        const dimNameParts = dimColumns.map((name, dimIndex) => {
          const col = dimCols[dimIndex];
          const value = col?.get(rowIndex);
          return value == null ? '' : String(value);
        });
        const dimKey = `${dimNameParts.join(' ').trim()} `;
        const matchIndex = matchedBeforeWindow;

        const totalValues = periodColumns.map((name, periodIndex) => {
          const col = periodCols[periodIndex];
          const raw = col?.get(rowIndex);
          const numeric = typeof raw === 'number' ? raw : Number(raw ?? 0);
          return { name, value: Number.isFinite(numeric) ? numeric : 0 };
        });

        rows.push({
          id: `${indicatorId}__${matchIndex}__${dimNameParts.join('_')}`,
          name: dimKey,
          chevronType: 'child',
          total_values: totalValues,
          indicatorId,
          parentId: indicatorId,
          type: dataType,
        });
        matchedBeforeWindow += 1;
      }
    }
  }

  return rows;
}

export type ArrowVirtualSpacerRow = {
  id: string;
  chevronType: 'arrow-spacer';
  spacerHeightPx: number;
  parentId: number;
};

/**
 * Placeholder row so ReactGrid scroll height reflects unseen Arrow tuples.
 */
export function buildArrowVirtualSpacerRow(
  parentId: number,
  position: 'top' | 'bottom',
  heightPx: number,
): ArrowVirtualSpacerRow {
  return {
    id: `arrow-spacer-${position}-${parentId}`,
    chevronType: 'arrow-spacer',
    spacerHeightPx: heightPx,
    parentId,
  };
}

/**
 * Period columns = all fields except indicator + breakdown dims.
 */
export function inferPeriodColumns(
  table: Table,
  indicatorColumn: string,
  dimColumns: string[],
): string[] {
  const skip = new Set([indicatorColumn, ...dimColumns]);
  return table.schema.fields
    .map((field: Field) => field.name)
    .filter((name: string) => !skip.has(name));
}
